"use client"

import React from "react"
import { Bold, Heading, Italic, Link2, List, ListOrdered, Strikethrough } from "lucide-react"
import { cn } from "@/lib/utils"
import { computeMarkdownEdit, type MarkdownFormatAction } from "@/lib/markdown-format"

export type { MarkdownFormatAction }

/**
 * Замена через execCommand('insertText'): браузер шлёт настоящее input-событие (React onChange,
 * live-text совместного редактора) и кладёт правку в собственный undo поля.
 */
export function applyMarkdownFormat(el: HTMLTextAreaElement, action: MarkdownFormatAction) {
  if (el.disabled || el.readOnly) return
  const { start, end, text, selStart, selEnd } = computeMarkdownEdit(el.value, el.selectionStart, el.selectionEnd, action)
  el.focus()
  el.setSelectionRange(start, end)
  let ok = false
  try { ok = document.execCommand("insertText", false, text) } catch { ok = false }
  if (!ok || el.value.slice(start, start + text.length) !== text) {
    el.setRangeText(text, start, end, "end")
    el.dispatchEvent(new Event("input", { bubbles: true }))
  }
  el.setSelectionRange(selStart, selEnd)
}

/** Ctrl/⌘+B, Ctrl/⌘+I, Ctrl/⌘+K, Ctrl/⌘+Shift+X в textarea с markdown. true — сочетание обработано */
export function handleMarkdownShortcut(e: React.KeyboardEvent<HTMLTextAreaElement>): boolean {
  if (!(e.ctrlKey || e.metaKey) || e.altKey) return false
  const code = e.nativeEvent.code
  const action: MarkdownFormatAction | null =
    code === "KeyB" && !e.shiftKey ? "bold"
    : code === "KeyI" && !e.shiftKey ? "italic"
    : code === "KeyK" && !e.shiftKey ? "link"
    : code === "KeyX" && e.shiftKey ? "strike"
    : null
  if (!action) return false
  e.preventDefault()
  applyMarkdownFormat(e.currentTarget, action)
  return true
}

const BUTTONS: { action: MarkdownFormatAction; icon: React.ComponentType<{ className?: string }>; title: string }[] = [
  { action: "bold", icon: Bold, title: "Жирный (Ctrl+B)" },
  { action: "italic", icon: Italic, title: "Курсив (Ctrl+I)" },
  { action: "strike", icon: Strikethrough, title: "Зачёркнутый (Ctrl+Shift+X)" },
  { action: "heading", icon: Heading, title: "Заголовок" },
  { action: "bullets", icon: List, title: "Маркированный список" },
  { action: "numbers", icon: ListOrdered, title: "Нумерованный список" },
  { action: "link", icon: Link2, title: "Ссылка (Ctrl+K)" },
]

/**
 * Кнопки markdown-форматирования для textarea. Нажатие не забирает фокус у поля (mousedown
 * отменён), поэтому выделение сохраняется, а blur-сохранение поля не срабатывает.
 */
export function MarkdownToolbar({
  targetRef,
  className,
  size = "sm",
  disabled,
}: {
  targetRef: React.RefObject<HTMLTextAreaElement | null>
  className?: string
  size?: "sm" | "md"
  disabled?: boolean
}) {
  const btn = size === "md" ? "h-9 w-9" : "h-7 w-7"
  const icon = size === "md" ? "h-5 w-5" : "h-4 w-4"
  return (
    <div className={cn("flex items-center gap-0.5", className)} role="toolbar" aria-label="Форматирование">
      {BUTTONS.map(({ action, icon: Icon, title }) => (
        <button
          key={action}
          type="button"
          title={title}
          aria-label={title}
          disabled={disabled}
          data-md-action={action}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => { const el = targetRef.current; if (el) applyMarkdownFormat(el, action) }}
          className={cn(
            "flex flex-shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground active:bg-muted disabled:opacity-40",
            btn,
          )}
        >
          <Icon className={icon} />
        </button>
      ))}
    </div>
  )
}
